/**
 * Subject Authentication Store
 *
 * Provides authentication for credential subjects using:
 * 1. Ethereum wallet connection (MetaMask, WalletConnect)
 * 2. WebAuthn/Passkeys for passwordless login
 * 3. Optional email-based authentication
 *
 * The authentication is separate from the issuer portal authentication.
 * Subjects authenticate to manage their credentials and generate proofs.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";

// ============================================================================
// Event Listener Management
// ============================================================================

// Store event handlers for cleanup
let accountsChangedHandler: ((accounts: unknown) => void) | null = null;
let chainChangedHandler: (() => void) | null = null;

/**
 * Remove Ethereum event listeners to prevent memory leaks
 */
function cleanupEthereumListeners(): void {
  if (window.ethereum) {
    if (accountsChangedHandler) {
      window.ethereum.removeListener?.("accountsChanged", accountsChangedHandler);
      accountsChangedHandler = null;
    }
    if (chainChangedHandler) {
      window.ethereum.removeListener?.("chainChanged", chainChangedHandler);
      chainChangedHandler = null;
    }
  }
}

// ============================================================================
// Types
// ============================================================================

export type AuthMethod = "wallet" | "passkey" | "email" | "none";

export interface WalletConnection {
  address: string;
  chainId: number;
  connectedAt: string;
  provider: "metamask" | "walletconnect" | "unknown";
}

export interface PasskeyCredential {
  id: string;
  publicKey: string;
  createdAt: string;
  lastUsed: string;
  name?: string;
}

export interface SubjectIdentity {
  /** Unique identifier for the subject */
  id: string;
  /** Primary authentication method */
  primaryAuthMethod: AuthMethod;
  /** Connected Ethereum wallet addresses */
  wallets: WalletConnection[];
  /** Registered passkeys */
  passkeys: PasskeyCredential[];
  /** Optional email for recovery */
  email?: string;
  /** When the identity was created */
  createdAt: string;
  /** When the identity was last authenticated */
  lastAuthenticatedAt: string;
  /** Display name (optional) */
  displayName?: string;
}

export interface AuthState {
  // Authentication state
  isAuthenticated: boolean;
  identity: SubjectIdentity | null;
  currentWallet: WalletConnection | null;
  authMethod: AuthMethod;
  isConnecting: boolean;
  error: string | null;
  /** Explicit disconnection flag - prevents auto-reconnect on reload */
  wasDisconnected: boolean;

  // Actions
  connectWallet: () => Promise<boolean>;
  disconnectWallet: () => void;
  registerPasskey: (name?: string) => Promise<boolean>;
  authenticateWithPasskey: () => Promise<boolean>;
  removePasskey: (id: string) => void;
  setEmail: (email: string) => void;
  setDisplayName: (name: string) => void;
  signMessage: (message: string) => Promise<string | null>;
  logout: () => void;
  clearError: () => void;
}

// ============================================================================
// Ethereum Provider Types
// ============================================================================

interface EthereumProvider {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
  on: (event: string, handler: (...args: unknown[]) => void) => void;
  removeListener: (event: string, handler: (...args: unknown[]) => void) => void;
  isMetaMask?: boolean;
}

declare global {
  interface Window {
    ethereum?: EthereumProvider;
  }
}

// ============================================================================
// WebAuthn Helpers
// ============================================================================

/**
 * Check if WebAuthn is supported in the browser
 */
export function isWebAuthnSupported(): boolean {
  return !!(
    window.PublicKeyCredential &&
    typeof window.PublicKeyCredential === "function"
  );
}

/**
 * Check if platform authenticator (TouchID, FaceID, Windows Hello) is available
 */
export async function isPlatformAuthenticatorAvailable(): Promise<boolean> {
  if (!isWebAuthnSupported()) return false;
  try {
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/**
 * Generate a random challenge for WebAuthn
 */
function generateChallenge(): ArrayBuffer {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return array.buffer as ArrayBuffer;
}

/**
 * Convert ArrayBuffer to base64 string
 */
function bufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Convert base64 string to ArrayBuffer
 */
function base64ToBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer as ArrayBuffer;
}

// ============================================================================
// Store Implementation
// ============================================================================

export const useSubjectAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      isAuthenticated: false,
      identity: null,
      currentWallet: null,
      authMethod: "none",
      isConnecting: false,
      error: null,
      wasDisconnected: false,

      // Connect Ethereum wallet
      connectWallet: async () => {
        set({ isConnecting: true, error: null });

        try {
          if (!window.ethereum) {
            throw new Error("No Ethereum wallet detected. Please install MetaMask or another Web3 wallet.");
          }

          // Request account access
          const accounts = await window.ethereum.request({
            method: "eth_requestAccounts",
          }) as string[];

          if (!accounts || accounts.length === 0) {
            throw new Error("No accounts found. Please connect your wallet.");
          }

          // Get chain ID
          const chainId = await window.ethereum.request({
            method: "eth_chainId",
          }) as string;

          const address = accounts[0].toLowerCase();
          const chainIdNum = parseInt(chainId, 16);

          const walletConnection: WalletConnection = {
            address,
            chainId: chainIdNum,
            connectedAt: new Date().toISOString(),
            provider: window.ethereum.isMetaMask ? "metamask" : "unknown",
          };

          // Create or update identity
          const existingIdentity = get().identity;
          const identity: SubjectIdentity = existingIdentity
            ? {
                ...existingIdentity,
                wallets: existingIdentity.wallets.some((w) => w.address === address)
                  ? existingIdentity.wallets.map((w) =>
                      w.address === address ? walletConnection : w
                    )
                  : [...existingIdentity.wallets, walletConnection],
                lastAuthenticatedAt: new Date().toISOString(),
              }
            : {
                id: `subj_${address.slice(2, 10)}`,
                primaryAuthMethod: "wallet",
                wallets: [walletConnection],
                passkeys: [],
                createdAt: new Date().toISOString(),
                lastAuthenticatedAt: new Date().toISOString(),
              };

          set({
            isAuthenticated: true,
            identity,
            currentWallet: walletConnection,
            authMethod: "wallet",
            isConnecting: false,
            wasDisconnected: false,
          });

          // Clean up any existing listeners before adding new ones
          cleanupEthereumListeners();

          // Listen for account changes
          accountsChangedHandler = (newAccounts: unknown) => {
            const accs = newAccounts as string[];
            if (!accs || accs.length === 0) {
              get().disconnectWallet();
            } else {
              // Update with new account
              get().connectWallet();
            }
          };
          window.ethereum.on("accountsChanged", accountsChangedHandler);

          // Listen for chain changes
          chainChangedHandler = () => {
            // Refresh connection
            get().connectWallet();
          };
          window.ethereum.on("chainChanged", chainChangedHandler);

          return true;
        } catch (err) {
          set({
            isConnecting: false,
            error: err instanceof Error ? err.message : "Failed to connect wallet",
          });
          return false;
        }
      },

      // Disconnect wallet
      disconnectWallet: () => {
        // Clean up Ethereum event listeners
        cleanupEthereumListeners();
        
        // Clear wallet connection and set explicit disconnect flag
        // This prevents auto-reconnect on page reload
        set({
          currentWallet: null,
          authMethod: "none",
          isAuthenticated: false,
          wasDisconnected: true,
        });
      },

      // Register a new passkey
      registerPasskey: async (name?: string) => {
        if (!isWebAuthnSupported()) {
          set({ error: "WebAuthn is not supported in this browser" });
          return false;
        }

        const identity = get().identity;
        if (!identity) {
          set({ error: "Please connect a wallet first to register a passkey" });
          return false;
        }

        try {
          set({ isConnecting: true, error: null });

          const challenge = generateChallenge();
          const userIdArray = new TextEncoder().encode(identity.id);
          const userId = userIdArray.buffer as ArrayBuffer;

          const createOptions: PublicKeyCredentialCreationOptions = {
            challenge,
            rp: {
              name: "ZKCN Wallet",
              id: window.location.hostname,
            },
            user: {
              id: userId,
              name: identity.displayName || identity.id,
              displayName: identity.displayName || `ZKCN Subject ${identity.id.slice(5, 13)}`,
            },
            pubKeyCredParams: [
              { alg: -7, type: "public-key" }, // ES256
              { alg: -257, type: "public-key" }, // RS256
            ],
            timeout: 60000,
            attestation: "none",
            authenticatorSelection: {
              authenticatorAttachment: "platform",
              userVerification: "required",
              residentKey: "required",
            },
          };

          const credential = (await navigator.credentials.create({
            publicKey: createOptions,
          })) as PublicKeyCredential | null;

          if (!credential) {
            throw new Error("Failed to create passkey");
          }

          const response = credential.response as AuthenticatorAttestationResponse;

          const passkeyCredential: PasskeyCredential = {
            id: bufferToBase64(credential.rawId),
            publicKey: bufferToBase64(response.getPublicKey() || new ArrayBuffer(0)),
            createdAt: new Date().toISOString(),
            lastUsed: new Date().toISOString(),
            name: name || `Passkey ${identity.passkeys.length + 1}`,
          };

          set({
            identity: {
              ...identity,
              passkeys: [...identity.passkeys, passkeyCredential],
            },
            isConnecting: false,
          });

          return true;
        } catch (err) {
          set({
            isConnecting: false,
            error: err instanceof Error ? err.message : "Failed to register passkey",
          });
          return false;
        }
      },

      // Authenticate with a passkey
      authenticateWithPasskey: async () => {
        if (!isWebAuthnSupported()) {
          set({ error: "WebAuthn is not supported in this browser" });
          return false;
        }

        const identity = get().identity;
        if (!identity || identity.passkeys.length === 0) {
          set({ error: "No passkeys registered. Please register a passkey first." });
          return false;
        }

        try {
          set({ isConnecting: true, error: null });

          const challenge = generateChallenge();

          const getOptions: PublicKeyCredentialRequestOptions = {
            challenge,
            timeout: 60000,
            rpId: window.location.hostname,
            userVerification: "required",
            allowCredentials: identity.passkeys.map((pk) => ({
              type: "public-key" as const,
              id: base64ToBuffer(pk.id),
            })),
          };

          const credential = (await navigator.credentials.get({
            publicKey: getOptions,
          })) as PublicKeyCredential | null;

          if (!credential) {
            throw new Error("Authentication cancelled or failed");
          }

          // Update last used time for the passkey
          const usedPasskeyId = bufferToBase64(credential.rawId);
          const updatedPasskeys = identity.passkeys.map((pk) =>
            pk.id === usedPasskeyId
              ? { ...pk, lastUsed: new Date().toISOString() }
              : pk
          );

          set({
            isAuthenticated: true,
            identity: {
              ...identity,
              passkeys: updatedPasskeys,
              lastAuthenticatedAt: new Date().toISOString(),
            },
            authMethod: "passkey",
            isConnecting: false,
          });

          return true;
        } catch (err) {
          set({
            isConnecting: false,
            error: err instanceof Error ? err.message : "Failed to authenticate with passkey",
          });
          return false;
        }
      },

      // Remove a passkey
      removePasskey: (id: string) => {
        const identity = get().identity;
        if (!identity) return;

        set({
          identity: {
            ...identity,
            passkeys: identity.passkeys.filter((pk) => pk.id !== id),
          },
        });
      },

      // Set recovery email
      setEmail: (email: string) => {
        const identity = get().identity;
        if (!identity) return;

        set({
          identity: {
            ...identity,
            email,
          },
        });
      },

      // Set display name
      setDisplayName: (name: string) => {
        const identity = get().identity;
        if (!identity) return;

        set({
          identity: {
            ...identity,
            displayName: name,
          },
        });
      },

      // Sign a message with the connected wallet
      signMessage: async (message: string) => {
        const wallet = get().currentWallet;
        if (!wallet || !window.ethereum) {
          set({ error: "No wallet connected" });
          return null;
        }

        try {
          const signature = await window.ethereum.request({
            method: "personal_sign",
            params: [message, wallet.address],
          }) as string;

          return signature;
        } catch (err) {
          set({
            error: err instanceof Error ? err.message : "Failed to sign message",
          });
          return null;
        }
      },

      // Logout
      logout: () => {
        set({
          isAuthenticated: false,
          currentWallet: null,
          authMethod: "none",
          error: null,
          wasDisconnected: true,
        });
      },

      // Clear error
      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: "zkcn-subject-auth",
      version: 3, // Bumped version to handle wasDisconnected
      partialize: (state) => ({
        identity: state.identity,
        // Also persist auth state for smoother UX and E2E testing
        isAuthenticated: state.isAuthenticated,
        currentWallet: state.currentWallet,
        authMethod: state.authMethod,
        wasDisconnected: state.wasDisconnected,
      }),
      // Migrate from older versions
      migrate: (persistedState: unknown, version: number) => {
        if (version === 0 || version === 1) {
          // Old version - just return identity, let rehydration set auth state
          const state = persistedState as { identity?: SubjectIdentity };
          return {
            identity: state?.identity || null,
            isAuthenticated: false,
            currentWallet: null,
            authMethod: "none" as AuthMethod,
            wasDisconnected: false,
          };
        }
        if (version === 2) {
          // Version 2 didn't have wasDisconnected
          const state = persistedState as {
            identity: SubjectIdentity | null;
            isAuthenticated: boolean;
            currentWallet: WalletConnection | null;
            authMethod: AuthMethod;
          };
          return {
            ...state,
            wasDisconnected: false,
          };
        }
        return persistedState as {
          identity: SubjectIdentity | null;
          isAuthenticated: boolean;
          currentWallet: WalletConnection | null;
          authMethod: AuthMethod;
          wasDisconnected: boolean;
        };
      },
      // On rehydration, restore auth state if identity exists AND user didn't explicitly disconnect
      onRehydrateStorage: () => (state) => {
        // If user explicitly disconnected, don't auto-reconnect
        if (state?.wasDisconnected) {
          return;
        }
        
        if (state && state.identity && state.identity.wallets.length > 0) {
          // If we have an identity with wallets, ensure isAuthenticated is set
          // This handles cases where the wallet was connected in a previous session
          const hasValidWallet = state.currentWallet && 
            state.identity.wallets.some(w => w.address === state.currentWallet?.address);
          if (!hasValidWallet && state.identity.wallets.length > 0) {
            // Restore current wallet from identity
            state.currentWallet = state.identity.wallets[0];
            state.authMethod = "wallet";
            state.isAuthenticated = true;
          }
        }
      },
    }
  )
);

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Format wallet address for display (0x1234...5678)
 */
export function formatAddress(address: string): string {
  if (!address) return "";
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

/**
 * Get chain name from chain ID
 */
export function getChainName(chainId: number): string {
  const chains: Record<number, string> = {
    1: "Ethereum Mainnet",
    11155111: "Sepolia Testnet",
    84532: "Base Sepolia",
    8453: "Base Mainnet",
    421614: "Arbitrum Sepolia",
    42161: "Arbitrum One",
  };
  return chains[chainId] || `Chain ${chainId}`;
}

/**
 * Check if wallet is on a supported chain
 */
export function isSupportedChain(chainId: number): boolean {
  const supportedChains = [11155111, 84532, 421614]; // Testnets for now
  return supportedChains.includes(chainId);
}
